/**
 * MainTab — landing navigation for ManagePlatform (CloudGM customers vs Merchants).
 * Extracted from ManagePlatform.js
 *
 * Iter52 fix: Unified card sizing + sequential numbered badges (1-N) for both
 * "إدارة المنصة" and "الحسابات والاشتراكات" sections so cards look consistent.
 */
import { motion } from 'framer-motion';
import { usePerms } from '../../../permissions/PermissionsContext';
import {
  Users, Briefcase, TrendingUp, Store, Shield, ShoppingCart,
  CreditCard, MessageSquare, ClipboardList, Package,
} from 'lucide-react';

/**
 * Reusable navigation card with a numbered badge in the top-start corner and an
 * optional secondary badge (screen code / count) in the top-end corner.
 */
function NavCard({
  number, color, icon: Icon, title, subtitle, onClick, testId,
  screenCode, countBadge, delay = 0,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ delay }}
      onClick={onClick}
      className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-opacity-60 transition-all duration-300 cursor-pointer text-center overflow-hidden"
      style={{ borderColor: `${color}30` }}
      data-testid={testId}
    >
      {/* Sequential number badge — only shown when caller passes `number` */}
      {number != null && (
        <div
          className="absolute top-3 start-3 w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs border z-10"
          style={{ backgroundColor: `${color}25`, borderColor: `${color}60`, color }}
          data-testid={`${testId}-number`}
        >
          {number}
        </div>
      )}

      {/* Secondary badge: screen-code (visual left/end) */}
      {screenCode && (
        <div className="absolute top-3 end-3 px-2 py-0.5 rounded-md" style={{ backgroundColor: `${color}15` }}>
          <span className="text-[10px] font-mono" style={{ color }}>{screenCode}</span>
        </div>
      )}

      {/* Count badge (overrides screenCode visually when present) */}
      {countBadge !== null && countBadge !== undefined && countBadge > 0 && (
        <span
          className="absolute top-3 end-3 text-white text-xs px-2 py-1 rounded-full font-bold z-10"
          style={{ backgroundColor: color }}
        >
          {countBadge}
        </span>
      )}

      {/* Icon */}
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 border"
        style={{ backgroundColor: `${color}15`, borderColor: `${color}30` }}
      >
        <Icon className="h-7 w-7" style={{ color }} />
      </div>

      {/* Title */}
      <h3 className="text-base font-bold text-white mb-1 group-hover:text-opacity-90 transition-colors">
        {title}
      </h3>

      {/* Subtitle */}
      {subtitle && <p className="text-xs text-[#71717A]">{subtitle}</p>}

      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${color}10 0%, transparent 70%)` }}
      />
    </motion.div>
  );
}

export default function MainTab({
  language,
  isCloudGM,
  users,
  pendingStores,
  approvedAgents,
  agentRequests = [],
  setActiveTab,
}) {
  const isAr = language === 'ar';
  const { canViewRoute } = usePerms();
  const basePath = isCloudGM ? '/manage/cloudgm' : '/manage/cloudgm-business';

  return (
    <div>
      {/* قسم إدارة المنصة */}
      <h2 className="text-lg font-bold text-white mb-4">
        {isAr ? 'إدارة المنصة' : 'Platform Management'}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {isCloudGM ? (
          <>
            {canViewRoute(`${basePath}?tab=cloudgm-users`) && (
            <NavCard
              number={1}
              color="#d4af37"
              icon={Users}
              title={isAr ? 'العملاء المسجلون' : 'Registered Customers'}
              subtitle={`${users.length} ${isAr ? 'عميل' : 'customers'}`}
              onClick={() => setActiveTab('cloudgm-users')}
              testId="btn-users"
              screenCode="CGM-M01"
            />
            )}
            {canViewRoute(`${basePath}?tab=services-categories`) && (
            <NavCard
              number={2}
              color="#d4af37"
              icon={Briefcase}
              title={isAr ? 'طلبات الخدمات' : 'Service Requests'}
              onClick={() => setActiveTab('services-categories')}
              testId="btn-services-categories"
              screenCode="CGM-M02"
              delay={0.05}
            />
            )}
            {canViewRoute(`${basePath}?tab=market-follow`) && (
            <NavCard
              number={3}
              color="#14b8a6"
              icon={TrendingUp}
              title={isAr ? 'متابعة السوق - الحسابات الشخصية' : 'Market Follow - Personal Accounts'}
              onClick={() => setActiveTab('market-follow')}
              testId="btn-market-follow"
              screenCode="CGM-M03"
              delay={0.1}
            />
            )}
          </>
        ) : (
          <>
            {canViewRoute(`${basePath}?tab=store-requests`) && (
            <NavCard
              color="#f97316"
              icon={ClipboardList}
              title={isAr ? 'طلبات تفعيل المتاجر' : 'Store Activation Requests'}
              subtitle={isAr ? 'مراجعة طلبات تفعيل المتاجر' : 'Review activation requests'}
              onClick={() => setActiveTab('store-requests')}
              testId="btn-store-requests-card"
              countBadge={pendingStores.length}
              screenCode="BIZ-M01"
            />
            )}
            {canViewRoute(`${basePath}?tab=stores`) && (
            <NavCard
              color="#10b981"
              icon={Store}
              title={isAr ? 'إدارة المتاجر' : 'Stores Management'}
              subtitle={isAr ? 'المتاجر الفعّالة (شخصية وشركات)' : 'Approved personal & company stores'}
              onClick={() => setActiveTab('stores')}
              testId="btn-stores-management"
              screenCode="BIZ-M02"
              delay={0.05}
            />
            )}
            {canViewRoute(`${basePath}?tab=agents-management`) && (
            <NavCard
              color="#a855f7"
              icon={Shield}
              title={isAr ? 'الوكلاء' : 'Agents'}
              subtitle={isAr ? 'إدارة الوكلاء والأكواد' : 'Manage agents & codes'}
              onClick={() => setActiveTab('agents-management')}
              testId="btn-agents-management"
              countBadge={(agentRequests || []).filter(r => (r.status || 'pending') === 'pending').length}
              screenCode="BIZ-M03"
              delay={0.1}
            />
            )}
            {canViewRoute(`${basePath}?tab=listings-management`) && (
            <NavCard
              color="#10b981"
              icon={ShoppingCart}
              title={isAr ? 'إدارة المعروضات' : 'Listings Management'}
              subtitle={isAr ? 'المتاجر الإلكترونية، العقارات، الآليات' : 'E-commerce, Real Estate, Vehicles'}
              onClick={() => setActiveTab('listings-management')}
              testId="btn-listings-management"
              screenCode="BIZ-M04"
              delay={0.15}
            />
            )}
            {canViewRoute(`${basePath}?tab=order-tracking`) && (
            <NavCard
              color="#f97316"
              icon={Package}
              title={isAr ? 'تتبّع الطلبات' : 'Order Tracking'}
              subtitle={isAr ? 'طلبات التجارة الإلكترونية' : 'E-commerce orders'}
              onClick={() => setActiveTab('order-tracking')}
              testId="btn-order-tracking"
              screenCode="BIZ-M07"
              delay={0.2}
            />
            )}
          </>
        )}
      </div>

      {/* Accounts & Subscriptions for Merchants */}
      {!isCloudGM && (
        <>
          <h2 className="text-lg font-bold text-white mb-4 mt-8">
            {isAr ? 'الحسابات والاشتراكات' : 'Accounts & Subscriptions'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {canViewRoute(`${basePath}?tab=accounts`) && (
            <NavCard
              color="#3b82f6"
              icon={Users}
              title={isAr ? 'الحسابات' : 'Accounts'}
              subtitle={`${users.length} ${isAr ? 'تاجر' : 'merchants'}`}
              onClick={() => setActiveTab('accounts')}
              testId="btn-accounts"
              screenCode="BIZ-M05"
            />
            )}
            {canViewRoute(`${basePath}?tab=subscriptions`) && (
            <NavCard
              color="#ec4899"
              icon={CreditCard}
              title={isAr ? 'الاشتراكات' : 'Subscriptions'}
              onClick={() => setActiveTab('subscriptions')}
              testId="btn-subscriptions"
              screenCode="BIZ-M06"
              delay={0.05}
            />
            )}
            {canViewRoute(`${basePath}?tab=support-tickets`) && (
            <NavCard
              color="#3b82f6"
              icon={MessageSquare}
              title={isAr ? 'تذاكر الدعم' : 'Support Tickets'}
              onClick={() => setActiveTab('support-tickets')}
              testId="btn-support-tickets"
              screenCode="BIZ-M08"
              delay={0.15}
            />
            )}
          </div>
        </>
      )}
    </div>
  );
}
